import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { motion } from 'framer-motion';

const fadeInSlideUp = keyframes`
    from { opacity: 0; transform: translateY(20px); }
    to { opacity: 1; transform: translateY(0); }
`;

const WishContainer = styled(motion.div)`
    background-color: rgba(255, 255, 255, 0.7);
    border: 1px solid #f48fb1;
    padding: 15px;
    border-radius: 10px;
    margin-bottom: 25px; /* Increased margin for more space */
    box-shadow: 0 3px 7px rgba(0, 0, 0, 0.08);
    animation: ${fadeInSlideUp} 0.6s ease-out forwards;
    backdrop-filter: blur(3px);
    display: flex;
    flex-direction: column; /* Changed to column for better layout */
    align-items: flex-start;
`;

const WishText = styled.div`
    margin-bottom: 10px;
`;

const Name = styled.strong`
    color: ${(props) => props.theme.primaryColor};
    font-weight: bold;
    margin-right: 8px;
`;

const ReactionContainer = styled.div`
    display: flex;
    align-items: center;
    margin-top: 10px;
`;

const LikeButton = styled.button`
    background: none;
    border: none;
    color: ${(props) => props.theme.primaryColor};
    cursor: pointer;
    font-size: 1.2em;
    padding: 5px;
    outline: none;
    display: flex;
    align-items: center;
    margin-right: 15px;

    &:hover {
        opacity: 0.8;
    }
`;

const LikeCount = styled.span`
    margin-left: 5px;
    font-size: 0.9em;
`;

const LikedMostLabel = styled.label`
    font-size: 0.9em;
    color: #777;
    margin-right: 8px;
`;

const LikedMostInput = styled.input`
    padding: 8px;
    border: 1px solid #ddd;
    border-radius: 5px;
    font-size: 0.9em;
    flex-grow: 1;
`;

function AnimatedWish({ name, message, animationDelay, id }) {
    const [likes, setLikes] = useState(0);
    const [liked, setLiked] = useState(false);
    const [likedMost, setLikedMost] = useState('');
    const localStorageKey = `wish-${id}-likes`;
    const localStorageLikedMostKey = `wish-${id}-liked-most`;
    const localStorageLikedKey = `wish-${id}-liked`;

    useEffect(() => {
        const storedLikes = localStorage.getItem(localStorageKey);
        if (storedLikes) {
            setLikes(parseInt(storedLikes, 10));
        }
        const storedLikedMost = localStorage.getItem(localStorageLikedMostKey);
        if (storedLikedMost) {
            setLikedMost(storedLikedMost);
        }
        const storedLiked = localStorage.getItem(localStorageLikedKey);
        if (storedLiked === 'true') {
            setLiked(true);
        }
    }, [id, localStorageKey, localStorageLikedMostKey, localStorageLikedKey]);

    const handleLike = () => {
        const newLikes = liked ? likes - 1 : likes + 1;
        setLikes(newLikes);
        setLiked(!liked);
        localStorage.setItem(localStorageKey, newLikes.toString());
        localStorage.setItem(localStorageLikedKey, (!liked).toString());
    };

    const handleLikedMostChange = (event) => {
        setLikedMost(event.target.value);
        localStorage.setItem(localStorageLikedMostKey, event.target.value);
    };

    return (
        <WishContainer
            style={{ animationDelay: `${animationDelay}s` }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, ease: 'easeOut' }}
        >
            <WishText>
                <Name>{name}:</Name> {message}
            </WishText>
            <ReactionContainer>
                <LikeButton onClick={handleLike} aria-label={liked ? 'Unlike' : 'Like'}>
                    {liked ? '❤️' : '🤍'}
                    <LikeCount>{likes}</LikeCount>
                </LikeButton>
                <LikedMostLabel htmlFor={`liked-most-${id}`}>What did you like most?</LikedMostLabel>
                <LikedMostInput
                    type="text"
                    id={`liked-most-${id}`}
                    value={likedMost}
                    onChange={handleLikedMostChange}
                    placeholder="e.g., The animation, Your kind words"
                />
            </ReactionContainer>
        </WishContainer>
    );
}

export default AnimatedWish;